import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteTrackedItemSetting, getTrackedItemSettings, getRivhitItemPricing, setTrackedItemSetting } from '../api/client';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import type { RivhitCustomer, RivhitItem } from '../context/RivhitDataContext';
import { fuzzySearchList } from '../lib/fuzzySearch';

function formatSyncAge(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const BATCH_SIZE = 40;

interface RivhitPriceListItem {
  price_list_id?: number;
  item_id?: number;
  discount_percent?: number;
  price_nis?: number;
  quantity?: number;
  item_name?: string;
  item_part_num?: string;
  original_price_nis?: number;
}

interface TrackedItemSetting {
  item_id: number;
  item_name?: string;
  catalog_number?: string;
  item_group: string;
  is_tracked: boolean;
  cost_price_nis?: number | null;
  blend_name?: string;
  is_coffee?: boolean;
}

function fuzzySearch(items: RivhitItem[], query: string): RivhitItem[] {
  return fuzzySearchList(items, query, (item) => [
    item.item_name,
    item.catalog_number,
    item.item_group_name,
    item.barcode,
  ]);
}

function formatCurrency(value?: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
  }).format(value);
}

function itemCatalogNumber(item: RivhitItem): string {
  return item.catalog_number || item.barcode || '—';
}

export default function Items() {
  const { t } = useTranslation();
  const {
    configured,
    items: allItems,
    itemsLoading,
    itemsUpdatedAt,
    ensureItemsLoaded,
    refreshItems,
    customers,
    customersLoading,
    ensureCustomersLoaded,
  } = useRivhitData();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<RivhitItem | null>(null);
  const [pricingItem, setPricingItem] = useState<RivhitItem | null>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [pricingRows, setPricingRows] = useState<RivhitPriceListItem[]>([]);
  const [trackedItems, setTrackedItems] = useState<Record<number, TrackedItemSetting>>({});
  const [trackedItemGroupDrafts, setTrackedItemGroupDrafts] = useState<Record<number, string>>({});
  const [trackedItemCostDrafts, setTrackedItemCostDrafts] = useState<Record<number, string>>({});
  const [savingTrackedItems, setSavingTrackedItems] = useState<Set<number>>(new Set());
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [hasPricingRequest, setHasPricingRequest] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!configured) return;
    ensureItemsLoaded();
  }, [configured, ensureItemsLoaded]);

  useEffect(() => {
    if (!configured) return;
    getTrackedItemSettings()
      .then((response) => {
        const nextItems: Record<number, TrackedItemSetting> = {};
        const nextDrafts: Record<number, string> = {};
        const nextCostDrafts: Record<number, string> = {};
        for (const item of response.data?.items ?? []) {
          nextItems[item.item_id] = item;
          nextDrafts[item.item_id] = item.item_group || item.blend_name || item.item_name || '';
          nextCostDrafts[item.item_id] = item.cost_price_nis == null ? '' : String(item.cost_price_nis);
        }
        setTrackedItems(nextItems);
        setTrackedItemGroupDrafts(nextDrafts);
        setTrackedItemCostDrafts(nextCostDrafts);
      })
      .catch((err) => {
        console.error('[Items] Failed to load tracked item settings:', err);
      });
  }, [configured]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const filtered = useMemo(() => fuzzySearch(allItems, debouncedQuery), [allItems, debouncedQuery]);
  const { visible: paged, hasMore, sentinelRef } = useInfiniteScroll(filtered, BATCH_SIZE);

  const customerOptions = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const base = q
      ? customers.filter((customer) => {
          const fields = [
            rivhitDisplayName(customer),
            customer.phone,
            customer.email,
            customer.city,
          ].filter(Boolean) as string[];
          return fields.some((field) => field.toLowerCase().includes(q));
        })
      : customers;
    return base.slice(0, 50);
  }, [customerQuery, customers]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.customer_id === selectedCustomerId) ?? null,
    [customers, selectedCustomerId],
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-fetch pricing when customer is selected
  const loadPricingFor = useCallback(async (itemId: number, custId: number) => {
    setPricingLoading(true);
    setPricingError(null);
    setHasPricingRequest(true);
    try {
      const response = await getRivhitItemPricing(itemId, custId);
      setPricingRows(response.data ?? []);
    } catch (err: any) {
      setPricingRows([]);
      setPricingError(err?.response?.data?.detail || err?.message || 'Failed to load pricing');
    } finally {
      setPricingLoading(false);
    }
  }, []);

  const selectCustomer = useCallback((customer: RivhitCustomer) => {
    setSelectedCustomerId(customer.customer_id);
    setCustomerQuery(rivhitDisplayName(customer));
    setDropdownOpen(false);
    setHighlightIndex(-1);
    if (pricingItem?.item_id && customer.customer_id) {
      loadPricingFor(pricingItem.item_id, customer.customer_id);
    }
  }, [pricingItem, loadPricingFor]);

  const clearCustomer = useCallback(() => {
    setSelectedCustomerId(null);
    setCustomerQuery('');
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleCustomerInputChange = useCallback((value: string) => {
    setCustomerQuery(value);
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(true);
    setHighlightIndex(-1);
  }, []);

  const handleCustomerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!dropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, customerOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && highlightIndex < customerOptions.length) {
      e.preventDefault();
      selectCustomer(customerOptions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
      setHighlightIndex(-1);
    }
  }, [dropdownOpen, highlightIndex, customerOptions, selectCustomer]);

  const openPricing = async (item: RivhitItem) => {
    setPricingItem(item);
    setCustomerQuery('');
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    setHighlightIndex(-1);
    await ensureCustomersLoaded();
  };

  const saveTrackedItemSetting = useCallback(async (item: RivhitItem, isTracked: boolean) => {
    if (!item.item_id) return;
    const itemId = item.item_id;
    setSavingTrackedItems((prev) => new Set(prev).add(itemId));
    try {
      if (!isTracked) {
        await deleteTrackedItemSetting(itemId);
        setTrackedItems((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        setTrackedItemGroupDrafts((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        setTrackedItemCostDrafts((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        return;
      }

      const itemGroup = (trackedItemGroupDrafts[itemId] || item.item_name || '').trim();
      const costDraft = (trackedItemCostDrafts[itemId] ?? '').trim();
      const parsedCost = costDraft === '' ? null : Number(costDraft);
      if (parsedCost != null && (!Number.isFinite(parsedCost) || parsedCost < 0)) return;
      const response = await setTrackedItemSetting(itemId, {
        item_name: item.item_name,
        catalog_number: item.catalog_number,
        item_group: itemGroup,
        is_tracked: true,
        cost_price_nis: parsedCost,
      });
      setTrackedItems((prev) => ({ ...prev, [itemId]: response.data }));
      setTrackedItemGroupDrafts((prev) => ({ ...prev, [itemId]: response.data.item_group || response.data.blend_name || itemGroup }));
      setTrackedItemCostDrafts((prev) => ({
        ...prev,
        [itemId]: response.data.cost_price_nis == null ? '' : String(response.data.cost_price_nis),
      }));
    } finally {
      setSavingTrackedItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [trackedItemGroupDrafts, trackedItemCostDrafts]);

  const closePricing = () => {
    setPricingItem(null);
    setCustomerQuery('');
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    setHighlightIndex(-1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('items.title')}</h1>
          <p className="mt-0.5 text-sm text-gray-500">{t('items.subtitle')}</p>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <button
            onClick={refreshItems}
            disabled={itemsLoading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={itemsLoading ? 'animate-spin' : ''}>🔄</span>
            {t('items.refresh')}
          </button>
          {itemsUpdatedAt && !itemsLoading && (
            <span className="text-xs text-gray-400">
              {t('common.lastSynced', 'Synced')} {formatSyncAge(itemsUpdatedAt)}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400">
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('items.searchPlaceholder')}
            className="w-full rounded-md border border-gray-300 bg-gray-50 py-2 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ltr:pl-10 ltr:pr-3 rtl:pl-3 rtl:pr-10"
          />
        </div>
        {debouncedQuery && !itemsLoading && (
          <p className="mt-2 text-xs text-gray-500">
            {filtered.length} {t('items.resultsFound')}
          </p>
        )}
      </div>

      {itemsLoading ? (
        <div className="p-8 text-center text-sm text-gray-400">{t('common.loading')}</div>
      ) : paged.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">{t('items.noResults')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {paged.map((item) => {
              const isSelected = selectedItem?.item_id === item.item_id;
              const trackedItemSetting = item.item_id ? trackedItems[item.item_id] : undefined;
              const isTracked = Boolean(trackedItemSetting);
              const isSavingTracked = item.item_id ? savingTrackedItems.has(item.item_id) : false;
              return (
                <div
                  key={`${item.item_id ?? item.catalog_number ?? item.item_name}`}
                  className={`rounded-lg border p-4 transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="min-w-0 flex-1 text-start"
                    >
                      <h3 className="truncate font-semibold text-gray-900">
                        {item.item_name || t('items.unnamedItem')}
                      </h3>
                      <div className="mt-2 space-y-1 text-xs text-gray-500">
                        <p>{t('items.catalogPrice')}: {formatCurrency(item.price)}</p>
                        <p>{t('items.group')}: {item.item_group_name || '—'}</p>
                        <p>{t('items.catalogNumber')}: {itemCatalogNumber(item)}</p>
                        <p>{t('items.barcode')}: {item.barcode || '—'}</p>
                      </div>
                    </button>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isTracked ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {isTracked ? t('items.coffeeTracked') : t('items.notCoffeeTracked')}
                      </span>
                      <button
                        onClick={() => openPricing(item)}
                        disabled={!item.item_id}
                        className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {t('items.viewPricing')}
                      </button>
                    </div>
                  </div>
                  {item.item_id && (
                    <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={isTracked}
                            disabled={isSavingTracked}
                            onChange={(event) => {
                              if (event.target.checked && item.item_id) {
                                setTrackedItemGroupDrafts((prev) => ({
                                  ...prev,
                                  [item.item_id!]: prev[item.item_id!] || item.item_name || '',
                                }));
                                setTrackedItemCostDrafts((prev) => ({
                                  ...prev,
                                  [item.item_id!]: prev[item.item_id!] ?? '',
                                }));
                              }
                              void saveTrackedItemSetting(item, event.target.checked);
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          {t('items.trackAsCoffee')}
                        </label>
                        <input
                          type="text"
                          value={trackedItemGroupDrafts[item.item_id] ?? trackedItemSetting?.item_group ?? trackedItemSetting?.blend_name ?? ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            setTrackedItemGroupDrafts((prev) => ({ ...prev, [item.item_id!]: value }));
                          }}
                          onBlur={() => {
                            if (isTracked) void saveTrackedItemSetting(item, true);
                          }}
                          disabled={!isTracked || isSavingTracked}
                          placeholder={t('items.blendNamePlaceholder')}
                          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-400 disabled:bg-gray-100 disabled:text-gray-400"
                        />
                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <span className="whitespace-nowrap">{t('items.unitCost')}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={trackedItemCostDrafts[item.item_id] ?? (trackedItemSetting?.cost_price_nis == null ? '' : String(trackedItemSetting.cost_price_nis))}
                            onChange={(event) => {
                              const value = event.target.value;
                              setTrackedItemCostDrafts((prev) => ({ ...prev, [item.item_id!]: value }));
                            }}
                            onBlur={() => {
                              if (isTracked) void saveTrackedItemSetting(item, true);
                            }}
                            disabled={!isTracked || isSavingTracked}
                            placeholder={t('items.unitCostPlaceholder')}
                            aria-label={t('items.unitCost')}
                            className="w-28 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-normal text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-400 disabled:bg-gray-100 disabled:text-gray-400"
                          />
                        </label>
                      </div>
                      <p className="mt-2 text-[11px] text-gray-400">{t('items.unitCostHint')}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <span className="text-sm text-gray-400">
                {paged.length} / {filtered.length}
              </span>
            </div>
          )}
        </>
      )}

      {pricingItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closePricing}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">{pricingItem.item_name || t('items.unnamedItem')}</h2>
                </div>
                <button
                  onClick={closePricing}
                  className="text-sm text-gray-400 transition-colors hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-4 overflow-y-auto px-6 py-4">
              {/* Catalog info section */}
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm sm:grid-cols-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.catalogPrice')}</p>
                  <p className="mt-1 text-base font-semibold text-gray-900">{formatCurrency(pricingItem.price)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.catalogNumber')}</p>
                  <p className="mt-1 font-medium text-gray-700">{itemCatalogNumber(pricingItem)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.group')}</p>
                  <p className="mt-1 font-medium text-gray-700">{pricingItem.item_group_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.barcode')}</p>
                  <p className="mt-1 font-medium text-gray-700">{pricingItem.barcode || '—'}</p>
                </div>
              </div>

              {/* Customer autocomplete search */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-700">{t('items.pricingForCustomer')}</p>
                <div className="relative mt-3" ref={dropdownRef}>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400">
                      🔍
                    </span>
                    <input
                      ref={inputRef}
                      type="text"
                      value={customerQuery}
                      onChange={(e) => handleCustomerInputChange(e.target.value)}
                      onFocus={() => { if (!selectedCustomerId) setDropdownOpen(true); }}
                      onKeyDown={handleCustomerKeyDown}
                      placeholder={t('items.customerSearchPlaceholder')}
                      className="w-full rounded-md border border-gray-300 bg-white py-2 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ltr:pl-10 ltr:pr-8 rtl:pl-8 rtl:pr-10"
                    />
                    {selectedCustomerId && (
                      <button
                        onClick={clearCustomer}
                        className="absolute inset-y-0 ltr:right-2 rtl:left-2 flex items-center text-gray-400 hover:text-gray-600"
                        aria-label="Clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {dropdownOpen && !selectedCustomerId && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {customersLoading ? (
                        <div className="px-4 py-3 text-sm text-gray-500">{t('items.loadingCustomers')}</div>
                      ) : customerOptions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400">
                          {customerQuery.trim() ? t('items.noCustomersFound') : t('items.typeToSearch')}
                        </div>
                      ) : (
                        customerOptions.map((customer, idx) => (
                          <button
                            key={customer.customer_id}
                            onClick={() => selectCustomer(customer)}
                            onMouseEnter={() => setHighlightIndex(idx)}
                            className={`flex w-full items-center justify-between px-4 py-2.5 text-start text-sm transition-colors ${
                              idx === highlightIndex
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span className="truncate font-medium">{rivhitDisplayName(customer)}</span>
                            {customer.price_list_id && (
                              <span className="ml-2 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                                {t('items.priceListShort')} #{customer.price_list_id}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t('items.selectedCustomer')}: {rivhitDisplayName(selectedCustomer)}
                    {selectedCustomer.price_list_id ? ` · ${t('items.priceListShort')} #${selectedCustomer.price_list_id}` : ''}
                  </p>
                )}
              </div>

              {pricingError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {pricingError}
                </div>
              )}

              {!pricingError && !pricingLoading && hasPricingRequest && selectedCustomerId && pricingRows.length === 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {t('items.noCustomerPricing')}
                </div>
              )}

              {pricingRows.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                        <th className="px-4 py-3 text-start font-medium">{t('items.quantityBreak')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.originalPrice')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.discount')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.customerPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {pricingRows.map((row, index) => (
                        <tr key={`${row.price_list_id}-${row.item_id}-${index}`}>
                          <td className="px-4 py-3 text-gray-700">{row.quantity ?? 1}</td>
                          <td className="px-4 py-3 text-end text-gray-600">{formatCurrency(row.original_price_nis)}</td>
                          <td className="px-4 py-3 text-end text-gray-600">
                            {row.discount_percent != null ? `${row.discount_percent}%` : '—'}
                          </td>
                          <td className="px-4 py-3 text-end font-medium text-gray-900">{formatCurrency(row.price_nis)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
